import { fetchDownloadDicomSeries } from '~/backend/connector-api/connector-components';
import { handleError } from '~/utils/errors';
import { fetchGetTaskError, fetchGetTaskResult } from './connector-components';

// The connector fetcher auto-parses JSON responses (connector-fetcher.ts:109)
// regardless of the caller's declared return type, so blob-returning endpoints
// like GetTaskResult return a parsed object when the worker file has
// Content-Type: application/json (e.g. optimization_objectives.json). Passing
// that object to `new File([obj], ...)` stringifies it to "[object Object]",
// which later breaks JSON.parse downstream. Re-serialize the object back into
// a Blob so the File carries the real bytes.
function toBlob(value: unknown): Blob {
    if (value instanceof Blob) {
        return value;
    }
    return new Blob([JSON.stringify(value)], {
        type: 'application/json',
    });
}

export const downloadDicomSeries = async (
    studyId: string,
    seriesId: string
): Promise<Blob | undefined> => {
    try {
        const variables = {
            pathParams: {
                studyId,
                seriesId,
            },
            queryParams: {},
        };

        const blob = await fetchDownloadDicomSeries(variables);
        return blob;
    } catch (error) {
        handleError(error, `Error downloading DICOM series: ${error}`);
    }
};

export type DownloadOutcome = {
    files: File[];
    errors: string[];
};

export const downloadErrors = async (
    id: string,
    errors: string[]
): Promise<DownloadOutcome> => {
    const outcomes = await Promise.allSettled(
        errors.map(async (path) => {
            const res = await fetchGetTaskError({
                pathParams: { taskID: id, errorPath: path },
            });
            return new File([toBlob(res)], path);
        })
    );

    const files: File[] = [];
    const downloadErrors: string[] = [];

    outcomes.forEach((outcome, i) => {
        if (outcome.status === 'fulfilled') {
            files.push(outcome.value);
        } else {
            downloadErrors.push(
                `Failed to download error file ${errors[i]} ` +
                    `for task ${id}: ${outcome.reason?.message || outcome.reason}`
            );
        }
    });

    return { files, errors: downloadErrors };
};

export const downloadResults = async (
    id: string,
    results: string[]
): Promise<DownloadOutcome> => {
    const outcomes = await Promise.allSettled(
        results.map(async (path) => {
            const res = await fetchGetTaskResult({
                pathParams: { taskID: id, resultPath: path },
            });
            return new File([toBlob(res)], path);
        })
    );

    const files: File[] = [];
    const downloadErrors: string[] = [];

    outcomes.forEach((outcome, i) => {
        if (outcome.status === 'fulfilled') {
            files.push(outcome.value);
        } else {
            downloadErrors.push(
                `Failed to download result file ${results[i]} ` +
                    `for task ${id}: ${outcome.reason?.message || outcome.reason}`
            );
        }
    });

    return { files, errors: downloadErrors };
};
